import { useCallback, useEffect, useMemo, useState } from "react";
import { Hairline } from "@/components/avant/Avant";
import { LayoutGroup, motion } from "framer-motion";
import { TopBar, Footer } from "@/components/z6/Chrome";
import { DishGrid } from "@/components/z6/DishCard";
import { CrossContact } from "@/components/z6/FilteredResults";
import { DishDetail } from "@/components/z6/DishDetail";
import { FilterPanel } from "@/components/z6/FilterPanel";
import { SoftBackground } from "@/components/z6/Pattern";
import { ALLERGENS, useFilters } from "@/context/FiltersContext";
import { getOnce } from "@/lib/api";
import { AllergenKey } from "@/components/z6/AllergenKey";
import { useAmbient } from "@/lib/perf";

const SECTIONS = ["Teas", "Tonics", "Matcha", "Coffee"];
const TEMPS = [["HOT", "Hot"], ["ICED", "Iced"], ["ROOM TEMP", "Room temp"]];
const NO_DRINK_FILTER = { cats: [], temps: [], plant: false };
// Drinks use their own panel: category, serving temperature, plant-based (explicit diet=vegan) plus the shared allergen exclusions.
const drinkOk = (d, avoid, f) => !d.allergens.some((a) => avoid.includes(a))
  && (!f.cats.length || f.cats.includes(d.section))
  && (!f.temps.length || (d.serves || []).some((s) => f.temps.includes(s.serve)))
  && (!f.plant || d.diet === "vegan");

export default function Beverages() {
  const [all, setAll] = useState(null);
  const [failed, setFailed] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [df, setDf] = useState(NO_DRINK_FILTER);
  const { filters, toggle } = useFilters();
  const setAccent = useAmbient((s) => s.setAccent);
  const open = useCallback((d) => setSelected(d), []);
  const fetchAll = useCallback(() => { setFailed(false); getOnce("/dishes", { course: "beverage" }).then((d) => setAll(d.dishes)).catch(() => setFailed(true)); }, []);
  useEffect(() => { setAccent("#0E9AAE"); fetchAll(); }, [setAccent, fetchAll]);
  const avoid = filters.avoid.filter((a) => ALLERGENS.includes(a));
  // With a temperature filter on, only the matching serve(s) and price show; stored data is untouched.
  const drinks = useMemo(() => (all || []).filter((d) => drinkOk(d, avoid, df))
    .map((d) => (df.temps.length ? { ...d, serves: d.serves.filter((s) => df.temps.includes(s.serve)) } : d)), [all, avoid.join(), df]); // eslint-disable-line react-hooks/exhaustive-deps
  const activeCount = avoid.length + df.cats.length + df.temps.length + (df.plant ? 1 : 0);
  const clear = () => { avoid.forEach((a) => toggle("avoid", a)); setDf(NO_DRINK_FILTER); };
  return (
    <div data-testid="beverages-page" className="min-h-screen">
      <TopBar onOpenFilters={() => setFilterOpen(true)} />
      <LayoutGroup>
        <main className="mx-auto max-w-[1400px] space-y-14 px-5 pt-8 sm:px-8">
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-[30px] bg-white/85"
            style={{ boxShadow: "inset 0 0 0 1px rgba(14,154,174,0.25), 0 40px 90px -40px rgba(14,154,174,0.4)" }}>
            <SoftBackground testId="beverages-hero-bg" />
            <div className="relative max-w-3xl px-6 py-5 sm:px-9 sm:py-6">
              <p className="font-ui text-[15px] font-normal uppercase tracking-[0.32em] text-[#B8326C]">Teas · Tonics · Matcha · Coffee</p>
              <h1 data-testid="beverages-title" className="mt-2 font-display text-[56px] leading-none text-[#0B2E44]">Bever<span className="italic">ages</span></h1>
              <div className="mt-3"><AllergenKey testId="beverages-allergen-key" /></div>
              {activeCount > 0 && (
                <p data-testid="beverages-filter-note" className="font-ui mt-5 text-[15px] text-[#0B2E44]">Showing drinks that match your filters · <button onClick={clear} className="underline underline-offset-4">Clear</button></p>
              )}
            </div>
          </motion.section>
          {activeCount > 0 && <CrossContact />}
          {failed && (
            <div data-testid="beverages-error" role="alert" className="flex flex-wrap items-center gap-4">
              <p className="font-ui text-[16px] text-[#0B2E44]">We couldn't load the drinks. Please check your connection.</p>
              <button type="button" data-testid="beverages-retry" onClick={fetchAll} className="z6-btn z6-btn--pink">Try <em>again</em></button>
            </div>
          )}
          {!all && !failed && <div data-testid="beverages-skeleton" aria-busy="true" className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((k) => <div key={k} className="aspect-[3/4] animate-pulse rounded-[22px] bg-white/60 motion-reduce:animate-none" />)}</div>}
          {all && activeCount > 0 && !drinks.length && <p data-testid="beverages-no-match" className="font-display text-3xl text-[#0B2E44]">Nothing matches, ask your waiter</p>}
          {all && SECTIONS.map((s) => {
            const list = drinks.filter((d) => d.section === s);
            return list.length ? (
              <section key={s} data-testid={`beverages-section-${s.toLowerCase()}`} className="space-y-5">
                <div className="flex items-baseline gap-4">
                  <span className="font-ui text-[15px] font-normal tracking-[0.3em] text-[#B8326C]">{String(SECTIONS.indexOf(s) + 1).padStart(2, "0")}</span>
                  <h2 className="font-display text-[40px] leading-none text-[#0B2E44]">{s}</h2>
                  <Hairline className="flex-1 self-center" />
                </div>
                <DishGrid dishes={list} onOpen={open} testId={`beverages-grid-${s.toLowerCase()}`} />
              </section>
            ) : null;
          })}
        </main>
        <DishDetail dish={selected} onClose={() => setSelected(null)} />
      </LayoutGroup>
      <Footer orderLinks />
      <FilterPanel open={filterOpen} onClose={() => setFilterOpen(false)} resultCount={drinks.length} drinks={{ f: df, set: setDf, sections: SECTIONS, temps: TEMPS, onClear: clear }} />
    </div>
  );
}
